import React, { useEffect, useState } from 'react';
import { Input, InputNumber, Radio, Select, Tag, RadioChangeEvent } from 'antd';
import { useSelector } from 'react-redux';
import { userSelector } from '../../store/user/slice';
import { decode as base64_decode } from 'js-base64';
import { expense, expenseCategory, initExpense } from '../../model/expense';
import { paymentType } from '../../model/master';
import { formatNumber } from '../../utils/util';
import DatePicker, { DateObject } from "react-multi-date-picker";
import InputIcon from "react-multi-date-picker/components/input_icon";
import { getDateFromDatePicker } from '../../utils/datepicker';
import { formatActor, UNKNOWN_ACTOR_SHORT } from '../../utils/audit-format';

const { TextArea } = Input;
const { Option } = Select;

const months = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const days = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

interface Props {
    expense: expense;
    categories: expenseCategory[];
    paymentTypes?: paymentType[];
    disabled?: boolean;
    onChange: (data: expense) => void;
}

const ExpenseDetail: React.FC<Props> = (props) => {
    const reduxUser = useSelector(userSelector);

    // Phase 7C-6C: both fallbacks here used to be the literal
    // 'ผู้จัดการ ร้านค้า'. With no signed-in user the form therefore
    // showed a specific manager's name as the person recording the
    // expense -- a name nobody had entered and nobody could verify.
    // The two real sources are kept; the invented one is not.
    const getLoggedInName = () => {
        if (reduxUser && reduxUser.name) return reduxUser.name;
        if (typeof window !== 'undefined') {
            const uuid = window.localStorage.getItem("uuid");
            if (uuid) {
                try {
                    const parsed = JSON.parse(base64_decode(uuid));
                    return formatActor(parsed.name || parsed.username || parsed.code, UNKNOWN_ACTOR_SHORT);
                } catch (e) {}
            }
        }
        return UNKNOWN_ACTOR_SHORT;
    };

    /**
     * Phase 7C-6C rev.2 §19: only a record that does not exist yet may be
     * pre-filled with the current session.
     *
     * This used to fill in the signed-in name whenever `recorded_by` was
     * empty, saved records included -- so opening someone else's older
     * expense, or any expense written before 7C-5 stamped an actor, put
     * TODAY'S VIEWER in the "ผู้บันทึกรายการ" box. Pressing save then wrote
     * that name to the ledger, which is how a viewer quietly becomes the
     * recorder of a document they never touched.
     */
    const isNewExpense = (candidate?: expense | null) =>
        !candidate || !Number(candidate.id);

    const withRecorder = (candidate?: expense | null): expense => {
        const next = candidate ? { ...candidate } : { ...initExpense };
        if (isNewExpense(candidate) && !next.recorded_by) {
            next.recorded_by = getLoggedInName();
        }
        return next;
    };

    const [data, setData] = useState<expense>(() => withRecorder(props.expense));

    useEffect(() => {
        setData(withRecorder(props.expense));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.expense, reduxUser]);

    const calculateTaxes = (
        amountVal: number,
        vatTypeVal: number,
        whtTypeVal: number
    ) => {
        let amt = Number(amountVal) || 0;
        let vatAmt = 0;
        let whtRate = 0;
        let whtAmt = 0;
        let netAmt = amt;

        // VAT Calculation
        if (vatTypeVal === 1) {
            // รวม VAT 7% (Inclusive)
            vatAmt = Number((amt * 7 / 107).toFixed(2));
        } else if (vatTypeVal === 2) {
            // แยก VAT 7% (Exclusive)
            vatAmt = Number((amt * 0.07).toFixed(2));
        }

        // WHT Calculation
        if (whtTypeVal === 1) {
            whtRate = 1;
            whtAmt = Number((amt * 0.01).toFixed(2));
        } else if (whtTypeVal === 2) {
            whtRate = 3;
            whtAmt = Number((amt * 0.03).toFixed(2));
        } else if (whtTypeVal === 3) {
            whtRate = 5;
            whtAmt = Number((amt * 0.05).toFixed(2));
        }

        // Net Amount
        if (vatTypeVal === 2) {
            netAmt = amt + vatAmt - whtAmt;
        } else {
            netAmt = amt - whtAmt;
        }

        return {
            vat_amount: vatAmt,
            wht_rate: whtRate,
            wht_amount: whtAmt,
            net_amount: Number(netAmt.toFixed(2))
        };
    };

    const updateAndNotify = (updated: expense) => {
        const taxes = calculateTaxes(updated.amount || 0, updated.vat_type || 0, updated.wht_type || 0);
        const finalObj = {
            ...updated,
            ...taxes
        };
        setData(finalObj);
        props.onChange(finalObj);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const key = e.target.name;
        const val = e.target.value;
        const updated: any = { ...data, [key]: val };
        updateAndNotify(updated);
    };

    const onAmountChange = (value: number | null) => {
        const amt = value || 0;
        const updated = { ...data, amount: amt };
        updateAndNotify(updated);
    };

    const onCategoryChange = (catId: number) => {
        const selected = props.categories.find(c => c.id === catId);
        const updated = {
            ...data,
            category_id: catId,
            category_name: selected ? selected.name : ''
        };
        updateAndNotify(updated);
    };

    const onPaymentTypeChange = (pId: number) => {
        let pName = 'เงินสด (Cash)';
        if (pId === 2) pName = 'เงินโอน (Bank Transfer)';
        if (pId === 3) pName = 'สแกน QR PromptPay';
        if (pId === 4) pName = 'บัตรเครดิต / เดบิต';

        if (props.paymentTypes && props.paymentTypes.length > 0) {
            const found = props.paymentTypes.find(p => p.id === pId);
            if (found) pName = found.name;
        }

        const updated = {
            ...data,
            payment_type_id: pId,
            payment_type_name: pName
        };
        updateAndNotify(updated);
    };

    const onVatTypeChange = (val: number) => {
        const updated = { ...data, vat_type: val };
        updateAndNotify(updated);
    };

    const onWhtTypeChange = (val: number) => {
        const updated = { ...data, wht_type: val };
        updateAndNotify(updated);
    };

    const onStatusChange = (e: RadioChangeEvent) => {
        const updated = { ...data, status: e.target.value };
        updateAndNotify(updated);
    };

    const onDateChange = (e: any) => {
        const dateStr = typeof e === 'number' ? e + '' : getDateFromDatePicker(e);
        const updated = { ...data, expense_date: dateStr };
        updateAndNotify(updated);
    };

    return (
        <div className='detailFormContainer' data-testid="expense-detail-form" style={{ padding: '4px' }}>
            {/* Header Document Number and Date */}
            {data.id && data.id > 0 && data.doc_no ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className='formGroup'>
                        <label className='formLabel'>เลขที่เอกสาร (Doc No)</label>
                        <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary, #475569)', padding: '6px 0' }} data-testid="display-expense-doc-no">
                            {data.doc_no}
                        </div>
                    </div>
                    <div className='formGroup'>
                        <label className='formLabel required'>วันที่จ่ายเงิน (Date)</label>
                        <div className='formInputDate'>
                            <DatePicker
                                months={months}
                                weekDays={days}
                                value={data.expense_date || new Date()}
                                onChange={onDateChange}
                                render={<InputIcon />}
                                format={"YYYY-MM-DD"}
                                disabled={props.disabled}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ marginBottom: '16px' }}>
                    <div className='formGroup'>
                        <label className='formLabel required'>วันที่จ่ายเงิน (Date)</label>
                        <div className='formInputDate'>
                            <DatePicker
                                months={months}
                                weekDays={days}
                                value={data.expense_date || new Date()}
                                onChange={onDateChange}
                                render={<InputIcon />}
                                format={"YYYY-MM-DD"}
                                disabled={props.disabled}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Category and Title */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '16px', marginBottom: '16px' }}>
                <div className='formGroup'>
                    <label className='formLabel required'>หมวดหมู่ค่าใช้จ่าย</label>
                    <Select
                        value={data.category_id}
                        onChange={onCategoryChange}
                        disabled={props.disabled}
                        style={{ width: '100%' }}
                    >
                        {props.categories.map(cat => (
                            <Option key={cat.id} value={cat.id}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        background: cat.color || '#FF7A59',
                                        display: 'inline-block'
                                    }} />
                                    <span>{cat.name}</span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className='formGroup'>
                    <label className='formLabel required'>ชื่อรายการค่าใช้จ่าย / คำอธิบาย</label>
                    <Input
                        name="title"
                        placeholder="เช่น ค่าน้ำประปา, ค่าถุงพลาสติก, ค่าช่างแอร์"
                        value={data.title}
                        onChange={onInputChange}
                        disabled={props.disabled}
                    />
                </div>
            </div>

            {/* Payee and Payment Channel */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className='formGroup'>
                    <label className='formLabel'>จ่ายให้แก่ (Payee / Vendor / บุคคล)</label>
                    <Input
                        name="payee"
                        placeholder="เช่น การไฟฟ้านครหลวง, บจก.สยามซัพพลาย, เงินสด"
                        value={data.payee || ''}
                        onChange={onInputChange}
                        disabled={props.disabled}
                    />
                </div>

                <div className='formGroup'>
                    <label className='formLabel required'>ช่องทางชำระเงิน</label>
                    <Select
                        value={data.payment_type_id || 1}
                        onChange={onPaymentTypeChange}
                        disabled={props.disabled}
                        style={{ width: '100%' }}
                    >
                        <Option value={1}>💵 เงินสด (Cash)</Option>
                        <Option value={2}>🏦 เงินโอน (Bank Transfer)</Option>
                        <Option value={3}>📱 สแกน QR PromptPay</Option>
                        <Option value={4}>💳 บัตรเครดิต / เดบิต</Option>
                    </Select>
                </div>
            </div>

            {/* Base Amount, VAT, WHT */}
            <div className="expense-tax-section">
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '14px', alignItems: 'flex-start' }}>
                    <div className='formGroup'>
                        <label className='formLabel required' style={{ fontWeight: 700 }}>
                            จำนวนเงิน (บาท)
                        </label>
                        <InputNumber
                            min={0}
                            step={1}
                            className="expense-amount-input"
                            style={{ width: '100%', fontSize: '16px', fontWeight: 700 }}
                            value={data.amount}
                            onChange={onAmountChange}
                            disabled={props.disabled}
                            placeholder="0.00"
                            data-testid="expense-amount-input"
                        />
                    </div>

                    <div className='formGroup'>
                        <label className='formLabel'>ภาษีมูลค่าเพิ่ม (VAT)</label>
                        <Select
                            value={data.vat_type || 0}
                            onChange={onVatTypeChange}
                            disabled={props.disabled}
                            style={{ width: '100%' }}
                        >
                            <Option value={0}>ไม่มี VAT (0%)</Option>
                            <Option value={1}>รวม VAT ในตัว (7%)</Option>
                            <Option value={2}>แยก VAT ต่างหาก (7%)</Option>
                        </Select>
                    </div>

                    <div className='formGroup'>
                        <label className='formLabel'>หัก ณ ที่จ่าย (WHT)</label>
                        <Select
                            value={data.wht_type || 0}
                            onChange={onWhtTypeChange}
                            disabled={props.disabled}
                            style={{ width: '100%' }}
                        >
                            <Option value={0}>ไม่หัก (0%)</Option>
                            <Option value={1}>หัก 1% (ค่าขนส่ง)</Option>
                            <Option value={2}>หัก 3% (ค่าบริการ/ซ่อม)</Option>
                            <Option value={3}>หัก 5% (ค่าเช่า)</Option>
                        </Select>
                    </div>
                </div>

                {/* Calculation breakdown summary */}
                <div className="expense-tax-calc-box" style={{ marginTop: '12px', padding: '12px 16px' }}>
                    <div className="expense-calc-row">
                        <span>ยอดก่อนภาษี (Base Amount):</span>
                        <span style={{ fontWeight: 600 }}>฿{formatNumber(data.amount || 0)}</span>
                    </div>
                    {data.vat_amount > 0 && (
                        <div className="expense-calc-row">
                            <span>
                                ภาษีมูลค่าเพิ่ม 7% ({data.vat_type === 1 ? 'รวมในตัว' : 'บวกเพิ่ม'}):
                            </span>
                            <span style={{ color: '#10B981', fontWeight: 600 }}>
                                {data.vat_type === 2 ? '+' : ''}฿{formatNumber(data.vat_amount)}
                            </span>
                        </div>
                    )}
                    {data.wht_amount && data.wht_amount > 0 ? (
                        <div className="expense-calc-row">
                            <span>ภาษีหัก ณ ที่จ่าย ({data.wht_rate || 3}%):</span>
                            <span style={{ color: '#EF4444', fontWeight: 600 }}>-฿{formatNumber(data.wht_amount)}</span>
                        </div>
                    ) : null}
                    <div className="expense-calc-row total">
                        <span>ยอดจ่ายสุทธิ (Net Total):</span>
                        <span>฿{formatNumber(data.net_amount || 0)}</span>
                    </div>
                </div>
            </div>

            {/* Recorded by & Status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className='formGroup'>
                    <label className='formLabel' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>ผู้บันทึกรายการ</span>
                        {/* The old hint said "อิงจากผู้เข้าสู่ระบบ" on every
                            record. On a saved one that is false -- the name
                            shown belongs to whoever recorded it, which may be
                            someone else entirely. */}
                        <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
                            {isNewExpense(props.expense)
                                ? '🔒 อิงจากผู้เข้าสู่ระบบ (แก้ไขไม่ได้)'
                                : '🔒 ผู้ที่บันทึกรายการนี้ (แก้ไขไม่ได้)'}
                        </span>
                    </label>
                    {/* §19: no `|| getLoggedInName()` on the value below. The
                        field used to fall back to the signed-in name even on a
                        saved record, so opening any pre-7C-5 expense showed
                        today's viewer as its recorder. A new expense gets its
                        preview from state instead (`withRecorder`). */}
                    <Input
                        name="recorded_by"
                        value={data.recorded_by || ''}
                        disabled={true}
                        className="expense-disabled-input"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            color: '#FFFFFF',
                            WebkitTextFillColor: '#FFFFFF',
                            fontWeight: 600,
                            cursor: 'not-allowed'
                        }}
                        placeholder={isNewExpense(props.expense)
                            ? 'ผู้บันทึกรายการ (อิงจากผู้ใช้งานปัจจุบัน)'
                            : 'ไม่ระบุผู้บันทึก (รายการก่อนเปิดใช้ระบบยืนยันตัวตน)'}
                        data-testid="expense-recorded-by-input"
                    />
                </div>

                <div className='formGroup'>
                    <label className='formLabel'>สถานะเอกสาร</label>
                    <Radio.Group
                        onChange={onStatusChange}
                        value={data.status !== undefined ? data.status : 1}
                        disabled={props.disabled}
                    >
                        <Radio value={1}>🟢 ปกติ / อนุมัติ</Radio>
                        <Radio value={0}>🔴 ยกเลิก</Radio>
                    </Radio.Group>
                </div>
            </div>

            {/* Remark */}
            <div className='formGroup'>
                <label className='formLabel'>หมายเหตุ / ข้อมูลเพิ่มเติม</label>
                <TextArea
                    name="remark"
                    rows={2}
                    placeholder="ระบุหมายเหตุ เช่น เลขที่ใบเสร็จรับเงินต้นทาง, รอบบิล, วัตถุประสงค์การใช้จ่าย..."
                    value={data.remark || ''}
                    onChange={onInputChange}
                    disabled={props.disabled}
                />
            </div>
        </div>
    );
};

export default ExpenseDetail;
